/**
 * ZEPTO-STYLE ANIMATED SEARCH PLACEHOLDER — Clean rebuild
 * Namespace: zsp-* (Zepto Search Placeholder) — deliberately unique
 * to rule out any collision with theme CSS or other scripts.
 *
 * DEFENSIVE DESIGN:
 *   - Every critical visibility property is set with setProperty(..., 'important')
 *     so nothing in the theme's CSS can silently override it.
 *   - Debug logging behind a flag — set ZSP_DEBUG = false once confirmed working.
 */
(function () {
  'use strict';

  var ZSP_DEBUG = true; /* ── set to false once this is confirmed working ── */

  function log() {
    if (ZSP_DEBUG && window.console) {
      console.log.apply(console, ['[ZSP]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  /* ── CONFIG ──────────────────────────────────────────────── */
  var KEYWORDS = [
    'jackets', 'boots', 'gloves', 'pants', 'protectors',
    'accessories', 'helmets', 'knee guards', 'riding gear'
  ];
  var STATIC_PREFIX = 'Search for "';
  var SUFFIX        = '"';
  var INTERVAL_MS   = 2000;
  var ANIM_MS       = 280;
  var ICON_COLOR    = '#6b7280';
  var TEXT_COLOR    = '#6b7280';
  /* ─────────────────────────────────────────────────────────── */

  var INPUT_SELECTORS = [
    'input.search_header__input',
    'input[name="q"][type="search"]',
    'input[name="q"]',
    '.aloSearch input',
    '#search_pupop input',
    'input[placeholder*="Search"]',
    'input[placeholder*="search"]',
    /* ── Added to catch mobile full-page predictive search ── */
    'input[type="search"]',
    'input[role="searchbox"]',
    'input[role="combobox"]',
    'form[role="search"] input[type="text"]',
    'predictive-search input',
    '.search-modal input',
    '.search_header input',
    // '[class*="search"] input[type="text"]',
    // '[class*="search"] input[type="search"]'
  ].join(', ');

  var instances = [];
  var timerRef  = null;

  /* ── Force a style property with !important, bypassing any
     theme CSS that might silently win otherwise ── */
  function forceStyle(el, prop, value) {
    el.style.setProperty(prop, value, 'important');
  }

  function syncVisibility(inst) {
    /* FIX: focus always takes priority — never show the overlay
       while the user is actively focused in the field, regardless
       of whether it currently has a value. Without this check,
       positionOverlay()'s periodic re-sync (needed for the mobile
       collapse fix) could re-show the overlay milliseconds after
       the focus handler hid it, causing doubled/ghosted text. */
    if (document.activeElement === inst.input) {
      forceStyle(inst.overlay, 'visibility', 'hidden');
      inst.paused = true;
      log('hidden — input is focused');
      return;
    }

    var hasValue = inst.input.value && inst.input.value.trim();
    if (hasValue) {
      forceStyle(inst.overlay, 'visibility', 'hidden');
      inst.paused = true;
      log('hidden — input has value:', inst.input.value);
    } else {
      forceStyle(inst.overlay, 'visibility', 'visible');
      inst.paused = false;
      log('visible — input is empty');
    }
  }

  function buildOverlay(input) {
    if (input.dataset.zspInit === '1') return;
    input.dataset.zspInit = '1';
    log('initializing input', input);

    input.setAttribute('placeholder', STATIC_PREFIX);

    var cs         = window.getComputedStyle(input);
    var padLeft    = cs.paddingLeft || '20px';
    var fontSize   = cs.fontSize    || '14px';
    var fontFamily = cs.fontFamily  || 'inherit';
    var fontWeight = cs.fontWeight  || '400';

    /* ── Overlay — every critical property forced with !important ── */
    var overlay = document.createElement('div');
    overlay.className = 'zsp-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    forceStyle(overlay, 'position', 'fixed');
    forceStyle(overlay, 'display', 'flex');
    forceStyle(overlay, 'align-items', 'center');
    forceStyle(overlay, 'padding-left', padLeft);
    forceStyle(overlay, 'pointer-events', 'none');
    forceStyle(overlay, 'z-index', '2147483647'); /* max safe z-index — always on top */
    forceStyle(overlay, 'overflow', 'hidden');
    forceStyle(overlay, 'white-space', 'nowrap');
    forceStyle(overlay, 'font-size', fontSize);
    forceStyle(overlay, 'font-family', fontFamily);
    forceStyle(overlay, 'font-weight', fontWeight);
    forceStyle(overlay, 'color', TEXT_COLOR);
    forceStyle(overlay, 'line-height', '1');
    forceStyle(overlay, 'opacity', '1');
    forceStyle(overlay, 'visibility', 'visible');
    forceStyle(overlay, 'background', 'transparent');
    forceStyle(overlay, 'margin', '0');
    forceStyle(overlay, 'border', 'none');
    forceStyle(overlay, 'transform', 'none');

    /* Icon */
    var icon = document.createElement('span');
    icon.className = 'zsp-icon';
    icon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="' + ICON_COLOR + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>';
    forceStyle(icon, 'position', 'absolute');
    forceStyle(icon, 'left', '18px');
    forceStyle(icon, 'top', '50%');
    forceStyle(icon, 'transform', 'translateY(-50%)');
    forceStyle(icon, 'display', 'flex');

    /* Prefix — static, never animates */
    var prefix = document.createElement('span');
    prefix.className = 'zsp-prefix';
    prefix.textContent = STATIC_PREFIX;
    forceStyle(prefix, 'flex-shrink', '0');
    forceStyle(prefix, 'display', 'inline-block');
    forceStyle(prefix, 'color', TEXT_COLOR);

    /* Slot — vertical clip window for the sliding word */
    var slot = document.createElement('span');
    slot.className = 'zsp-slot';
    forceStyle(slot, 'position', 'relative');
    forceStyle(slot, 'display', 'inline-flex');
    forceStyle(slot, 'align-items', 'center');
    forceStyle(slot, 'overflow-y', 'hidden');
    forceStyle(slot, 'overflow-x', 'visible');
    forceStyle(slot, 'height', '1.5em');
    forceStyle(slot, 'min-width', '80px');
    forceStyle(slot, 'flex-shrink', '0');

    var wordA = document.createElement('span');
    wordA.className = 'zsp-word zsp-active';
    wordA.textContent = KEYWORDS[0] + SUFFIX;
    styleWord(wordA, false);

    var wordB = document.createElement('span');
    wordB.className = 'zsp-word zsp-next';
    wordB.textContent = '';
    styleWord(wordB, true);

    slot.appendChild(wordA);
    slot.appendChild(wordB);
    overlay.appendChild(icon);
    overlay.appendChild(prefix);
    overlay.appendChild(slot);
    document.body.appendChild(overlay);

    var inst = { input: input, overlay: overlay, slot: slot, current: 0, paused: false };
    instances.push(inst);

    /* Clear stale value when a <details> search modal reopens */
    var detailsAncestor = input.closest('details');
    if (detailsAncestor) {
      detailsAncestor.addEventListener('toggle', function () {
        if (detailsAncestor.open) {
          input.value = '';
          syncVisibility(inst);
          positionOverlay(inst);
          log('details reopened — cleared value + repositioned');
        } else {
          /* Immediate hide on close — faster than waiting for
             ResizeObserver to notice the input shrank to 0. */
          forceStyle(inst.overlay, 'visibility', 'hidden');
          log('details closed — overlay force-hidden immediately');
        }
      });
    }

    positionOverlay(inst);
    syncVisibility(inst);

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        positionOverlay(inst);
      });
      ro.observe(input);
      inst._ro = ro;
    }

    /* Aggressive retry burst for the first 5 seconds — catches any
       timing edge case (hidden-then-shown wrappers, late layout, etc.) */
    var retryCount = 0;
    var retryTimer = setInterval(function () {
      retryCount++;
      positionOverlay(inst);
      if (retryCount >= 50) clearInterval(retryTimer); /* 50 × 100ms = 5s */
    }, 100);

    input.addEventListener('focus', function () {
      inst.paused = true;
      forceStyle(overlay, 'visibility', 'hidden');
    });
    input.addEventListener('blur', function () { syncVisibility(inst); });
    input.addEventListener('input', function () { syncVisibility(inst); });

    log('overlay built', overlay);
    return inst;
  }

  function styleWord(el, isNext) {
    forceStyle(el, 'position', 'absolute');
    forceStyle(el, 'left', '0');
    forceStyle(el, 'top', '50%');
    forceStyle(el, 'display', 'block');
    forceStyle(el, 'white-space', 'nowrap');
    forceStyle(el, 'line-height', '1');
    forceStyle(el, 'color', TEXT_COLOR);
    el.style.transform  = isNext ? 'translateY(calc(-50% + 130%))' : 'translateY(-50%)';
    el.style.opacity    = isNext ? '0' : '1';
    el.style.transition = 'none';
  }

  function positionOverlay(inst) {
    var rect = inst.input.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      /* FIX: input is hidden (collapsed mobile search drawer, closed
         <details> modal, etc.) — explicitly hide the overlay instead
         of just skipping the position update. Without this, the
         overlay stays frozen at its last known coordinates and
         floats over page content, since it's a separate element
         appended to document.body with no automatic link to the
         input's own visibility state. */
      forceStyle(inst.overlay, 'visibility', 'hidden');
      log('input hidden (zero size) — overlay force-hidden', rect);
      return;
    }
    forceStyle(inst.overlay, 'top', rect.top + 'px');
    forceStyle(inst.overlay, 'left', rect.left + 'px');
    forceStyle(inst.overlay, 'width', rect.width + 'px');
    forceStyle(inst.overlay, 'height', rect.height + 'px');

    /* Input just became visible/sized again — re-evaluate whether
       the overlay should show, based on the normal empty/has-value
       rule (not just blindly force it visible). */
    syncVisibility(inst);
  }

  function refreshSlotWidth(inst) {
    var ruler = document.createElement('span');
    var cs = window.getComputedStyle(inst.overlay);
    ruler.style.cssText = 'position:fixed;top:-9999px;left:-9999px;visibility:hidden;white-space:nowrap;font-size:' + cs.fontSize + ';font-family:' + cs.fontFamily + ';font-weight:' + cs.fontWeight + ';line-height:1;';
    document.body.appendChild(ruler);
    var maxW = 0;
    KEYWORDS.forEach(function (kw) {
      ruler.textContent = kw + SUFFIX;
      maxW = Math.max(maxW, ruler.offsetWidth);
    });
    document.body.removeChild(ruler);
    forceStyle(inst.slot, 'width', (maxW + 4) + 'px');
    forceStyle(inst.slot, 'min-width', (maxW + 4) + 'px');
  }

  function cycleInstance(inst) {
    if (inst.paused) return;
    if (getComputedStyle(inst.overlay).visibility === 'hidden') return;

    var nextIdx  = (inst.current + 1) % KEYWORDS.length;
    var nextText = KEYWORDS[nextIdx] + SUFFIX;
    var active = inst.slot.querySelector('.zsp-active');
    var next   = inst.slot.querySelector('.zsp-next');
    if (!active || !next) return;

    next.style.transition = 'none';
    next.textContent      = nextText;
    next.style.transform  = 'translateY(calc(-50% + 130%))';
    next.style.opacity    = '0';
    void next.offsetWidth;

    var trans = 'transform ' + ANIM_MS + 'ms cubic-bezier(0.25,0.46,0.45,0.94), opacity ' + ANIM_MS + 'ms ease';
    active.style.transition = trans;
    next.style.transition   = trans;
    active.style.transform = 'translateY(calc(-50% - 130%))';
    active.style.opacity   = '0';
    next.style.transform   = 'translateY(-50%)';
    next.style.opacity     = '1';

    setTimeout(function () {
      active.style.transition = 'none';
      active.style.transform  = 'translateY(calc(-50% + 130%))';
      active.style.opacity    = '0';
      active.textContent      = '';
      active.classList.remove('zsp-active');
      active.classList.add('zsp-next');
      next.classList.remove('zsp-next');
      next.classList.add('zsp-active');
      inst.current = nextIdx;
    }, ANIM_MS + 30);
  }

  function initAll() {
    var inputs = document.querySelectorAll(INPUT_SELECTORS);
    log('found inputs:', inputs.length);
    if (!inputs.length) { setTimeout(initAll, 500); return; }

    inputs.forEach(buildOverlay);

    var doMeasure = function () {
      instances.forEach(refreshSlotWidth);
      instances.forEach(positionOverlay);
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(doMeasure);
    } else {
      setTimeout(doMeasure, 300);
    }

    var scrollTicking = false;
    window.addEventListener('scroll', function () {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(function () {
        instances.forEach(positionOverlay);
        scrollTicking = false;
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      instances.forEach(refreshSlotWidth);
      instances.forEach(positionOverlay);
    });

    if (!timerRef) {
      timerRef = setInterval(function () {
        instances.forEach(cycleInstance);
      }, INTERVAL_MS);
    }

    log('init complete. Instances:', instances.length);
  }

  function observeDOM() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (mutations) {
      var added = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var found = node.matches && node.matches(INPUT_SELECTORS)
            ? [node]
            : Array.from(node.querySelectorAll ? node.querySelectorAll(INPUT_SELECTORS) : []);
          found.forEach(function (input) {
            if (!input.dataset.zspInit) { buildOverlay(input); added = true; }
          });
        });
      });
      if (added) {
        var doM = function () {
          instances.forEach(refreshSlotWidth);
          instances.forEach(positionOverlay);
        };
        document.fonts && document.fonts.ready ? document.fonts.ready.then(doM) : setTimeout(doM, 300);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    log('booting...');
    initAll();
    observeDOM();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();

})();